/**
 * Codeshare Helper Functions
 *
 * Utilities for managing codeshares that are stored separately from flight records
 */

import type { MasterFlightRecord } from './types';
import type { CodesharePayload } from '../services/redux/api/codeshare.api';

/**
 * Extracts codeshare data from stations for API submission
 * Codeshares are stored in a separate collection per sector
 *
 * @param record - Flight record with stations containing codeshare info
 * @param masterFlightId - The ID of the master flight (required for updates)
 * @returns Array of codeshare payloads to be sent to codeshare API
 */
export function extractCodesharesFromStations(
  record: MasterFlightRecord,
  masterFlightId?: string
): CodesharePayload[] {
  const codeshares: CodesharePayload[] = [];

  record.stations.forEach((station) => {
    // Only create codeshare if there are marketing flight numbers
    if (station.codeshareFlightNo && station.codeshareFlightNo.length > 0) {
      const sector = `${station.departureStation} ${station.arrivalStation}`;

      codeshares.push({
        masterflightRef: masterFlightId || '', // Will be filled after flight creation
        sector,
        codeshareFlightNo: station.codeshareFlightNo,

        // Auto-populated from master flight
        seasonId: record.seasonId,
        flightOwner: record.flightOwner,
        flightNo: record.flightNo,
        csStartDate: record.startDate,
        csEndDate: record.endDate,
        frequency: record.frequency,
        freqRate: record.freqRate || '', // Default to empty string for weekly (SSIM compliant)
      });
    }
  });

  return codeshares;
}

/**
 * Strips codeshare data from stations before sending to master flight API
 * Backend no longer stores codeshares in stations array
 *
 * @param record - Flight record to clean
 * @returns Cleaned flight record without codeshare data in stations
 */
export function stripCodesharesFromStations(record: MasterFlightRecord): MasterFlightRecord {
  return {
    ...record,
    stations: record.stations.map((station) => {
      const { codeshareFlightNo, ...stationWithoutCodeshares } = station;
      return stationWithoutCodeshares;
    }),
  };
}
